import Sidebar from '@/components/layout/sidebar';
import TaskModal from '@/components/modals/task-modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import type { Task, UserWithTeam } from '@shared/schema';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Plus, UserPlus } from 'lucide-react';
import { useMemo, useState } from 'react';

function startOfMonth(d: Date) {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfMonth(d: Date) {
  const x = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  x.setHours(23, 59, 59, 999);
  return x;
}

function addMonths(d: Date, n: number) {
  const x = new Date(d);
  x.setMonth(x.getMonth() + n);
  return x;
}

function fmtYYYYMMDD(d: Date) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
export function toYMD(d: Date) {
  return fmtYYYYMMDD(d);
}

function safeDate(val: any): Date | null {
  try {
    if (!val) return null;
    const d = new Date(val);
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
}

// Status color mapping for better visual distinction
const getStatusColor = (status: string) => {
  switch (status.toLowerCase()) {
    case 'pending':
      return 'bg-slate-100 text-slate-700 border-slate-200 hover:bg-slate-200';
    case 'started':
      return 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200';
    case 'working':
      return 'bg-indigo-100 text-indigo-700 border-indigo-200 hover:bg-indigo-200';
    case 'need_more_time':
      return 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200';
    case 'need_help':
      return 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-200';
    case 'pending_approval':
      return 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200';
    case 'complete':
      return 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-200';
  }
};

// Priority color mapping
const getPriorityColor = (priority: number) => {
  switch (priority) {
    case 1:
      return 'bg-red-50 border-l-4 border-red-500';
    case 2:
      return 'bg-yellow-50 border-l-4 border-yellow-500';
    case 3:
      return 'bg-green-50 border-l-4 border-green-500';
    default:
      return 'bg-gray-50 border-l-4 border-gray-300';
  }
};

export default function CalendarPage() {
  const [cursor, setCursor] = useState<Date>(new Date());
  const { user } = useAuth();
  const canManageStaff = user?.role === 'manager' || user?.role === 'admin';
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [showOnlyMyTasks, setShowOnlyMyTasks] = useState(false);

  // Minimal data needed for TaskModal
  const { data: users = [] } = useQuery<UserWithTeam[]>({
    queryKey: ['/api/users'],
    enabled: !!user,
  });
  const { data: teams = [] } = useQuery<any[]>({
    queryKey: ['/api/teams'],
    enabled: !!user,
  });

  const { data: tasksRaw } = useQuery<Task[] | any>({
    queryKey: ['/api/tasks'],
    staleTime: 30_000,
  });

  const allTasks: Task[] = Array.isArray(tasksRaw)
    ? tasksRaw
    : Array.isArray(tasksRaw?.data)
      ? tasksRaw.data
      : Array.isArray(tasksRaw?.tasks)
        ? tasksRaw.tasks
        : [];

  // Filter tasks based on admin's Me/All toggle
  const tasks: Task[] = useMemo(() => {
    if (user?.role === 'admin' && showOnlyMyTasks) {
      // Show only tasks assigned to the admin user
      return allTasks.filter(task => task.assignedTo === user.id);
    }
    return allTasks;
  }, [allTasks, user, showOnlyMyTasks]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);

  // Build days grid (Sun..Sat) with leading/trailing days to fill 6 weeks
  const { days, taskMap } = useMemo(() => {
    const map = new Map<string, Task[]>();
    if (Array.isArray(tasks)) {
      tasks.forEach(t => {
        const due = safeDate((t as any).dueDate);
        if (!due) return;
        const key = fmtYYYYMMDD(due);
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(t);
      });
    }

    const firstDayOfWeek = new Date(monthStart);
    const offset = firstDayOfWeek.getDay(); // 0=Sun .. 6=Sat
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - offset);

    const days: { date: Date; inMonth: boolean }[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      days.push({ date: d, inMonth: d.getMonth() === monthStart.getMonth() });
    }
    return { days, taskMap: map } as const;
  }, [tasks, monthStart]);

  const monthLabel = cursor.toLocaleString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="p-2 sm:p-4 md:p-6 space-y-4 sm:space-y-6">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="space-y-1">
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                Calendar
              </h2>
              <p className="text-slate-600 text-xs sm:text-sm hidden sm:block">
                Manage your tasks and deadlines with a visual calendar view
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2 sm:gap-3">
              {/* Admin Me/All Toggle */}
              {user?.role === 'admin' && (
                <div className="flex items-center gap-1 bg-white/80 backdrop-blur-sm rounded-lg p-1 shadow-sm border border-white/20">
                  <Button
                    variant={showOnlyMyTasks ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setShowOnlyMyTasks(true)}
                    className={`text-xs sm:text-sm px-2 sm:px-3 py-1 ${
                      showOnlyMyTasks
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    Me
                  </Button>
                  <Button
                    variant={!showOnlyMyTasks ? 'default' : 'ghost'}
                    size="sm"
                    onClick={() => setShowOnlyMyTasks(false)}
                    className={`text-xs sm:text-sm px-2 sm:px-3 py-1 ${
                      !showOnlyMyTasks
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    All
                  </Button>
                </div>
              )}
              {canManageStaff && (
                <Button
                  onClick={() => {
                    window.location.href = '/team-management';
                  }}
                  className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white shadow-lg hover:shadow-xl transition-all duration-200 text-xs sm:text-sm"
                  aria-label="Add staff"
                >
                  <UserPlus className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  <span className="hidden xs:inline">Add Staff</span>
                  <span className="xs:hidden">Add</span>
                </Button>
              )}
              <Button
                onClick={() => setIsTaskModalOpen(true)}
                className="bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white shadow-lg hover:shadow-xl transition-all duration-200"
                aria-label="New task"
              >
                <Plus className="w-4 h-4 mr-2" /> New Task
              </Button>
            </div>
          </div>
          {/* Navigation Controls - Sticky */}
          <div className="sticky top-0 z-10 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pb-2 sm:pb-4">
            <div className="flex items-center justify-between bg-white/70 backdrop-blur-sm rounded-xl p-2 sm:p-4 shadow-lg border border-white/20">
              <div className="flex items-center gap-1 sm:gap-3 flex-1">
                <Button
                  variant="outline"
                  onClick={() => setCursor(addMonths(cursor, -1))}
                  className="hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 p-2"
                  aria-label="Previous month"
                >
                  <ChevronLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
                <div className="flex-1 text-center min-w-0">
                  <h3 className="text-sm sm:text-base md:text-xl font-semibold text-slate-800 truncate">
                    {monthLabel}
                  </h3>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setCursor(addMonths(cursor, 1))}
                  className="hover:bg-blue-50 hover:border-blue-300 transition-all duration-200 p-2"
                  aria-label="Next month"
                >
                  <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setCursor(new Date())}
                  className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm px-2 sm:px-4"
                >
                  <span className="hidden sm:inline">Today</span>
                  <span className="sm:hidden">Now</span>
                </Button>
              </div>
            </div>

            {/* Day Headers - Sticky */}
            <div className="grid grid-cols-7 gap-1 sm:gap-3 mt-2 sm:mt-4">
              {'Sun Mon Tue Wed Thu Fri Sat'.split(' ').map(d => (
                <div
                  key={d}
                  className="text-xs sm:text-sm font-semibold uppercase tracking-wide text-slate-600 px-1 sm:px-3 py-1 sm:py-2 text-center bg-white/50 rounded-lg border border-white/20"
                >
                  <span className="hidden sm:inline">{d}</span>
                  <span className="sm:hidden">{d.charAt(0)}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-1 sm:gap-3">
            {days.map(({ date, inMonth }) => {
              const key = fmtYYYYMMDD(date);
              const list = taskMap.get(key) || [];
              const isToday = fmtYYYYMMDD(date) === fmtYYYYMMDD(new Date());
              const isWeekend = date.getDay() === 0 || date.getDay() === 6;

              return (
                <Card
                  key={key}
                  className={`p-1 sm:p-3 h-20 sm:h-32 md:h-44 overflow-hidden transition-all duration-200 hover:shadow-lg hover:scale-[1.02] ${
                    inMonth
                      ? isToday
                        ? 'bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 shadow-md'
                        : isWeekend
                          ? 'bg-gradient-to-br from-slate-50 to-gray-50 border border-slate-200'
                          : 'bg-white border border-slate-200 hover:border-blue-300'
                      : 'opacity-50 bg-slate-50 border border-slate-100'
                  }`}
                  onDoubleClick={() => {
                    setIsTaskModalOpen(true);
                    // prefill due date
                    const ymd = fmtYYYYMMDD(date);
                    // using setTimeout to ensure modal mounts before default is read
                    setTimeout(() => {
                      const input = document.querySelector(
                        "input[type='date']"
                      ) as HTMLInputElement | null;
                      if (input) input.value = ymd;
                    }, 0);
                  }}
                >
                  <div className="flex items-center justify-between mb-1 sm:mb-2">
                    <div
                      className={`text-xs sm:text-sm font-semibold ${
                        isToday
                          ? 'text-blue-700'
                          : inMonth
                            ? 'text-slate-800'
                            : 'text-slate-400'
                      }`}
                    >
                      {date.getDate()}
                    </div>
                    {list.length > 0 && (
                      <Badge
                        variant="secondary"
                        className={`text-[8px] sm:text-[10px] px-1 sm:px-2 py-0.5 sm:py-1 ${
                          list.length > 3
                            ? 'bg-red-100 text-red-700 border-red-200'
                            : list.length > 1
                              ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                              : 'bg-green-100 text-green-700 border-green-200'
                        }`}
                      >
                        {list.length}
                      </Badge>
                    )}
                  </div>
                  <div className="space-y-0.5 sm:space-y-1.5 overflow-y-auto max-h-12 sm:max-h-20 md:max-h-32 pr-1">
                    {list.slice(0, 4).map(t => {
                      const status = String((t as any).status || '')
                        .split('_')
                        .join(' ');
                      const priority = (t as any).priority || 0;

                      return (
                        <div
                          key={t.id}
                          className={`text-[10px] sm:text-xs px-1 sm:px-2 py-0.5 sm:py-1.5 rounded-md border transition-all duration-200 hover:shadow-sm ${getStatusColor(status)} ${getPriorityColor(priority)}`}
                        >
                          <div className="text-[8px] sm:text-[10px] mb-0.5 sm:mb-1 font-medium capitalize">
                            <span className="px-1 sm:px-1.5 py-0.5 rounded-full text-[7px] sm:text-[9px] font-semibold">
                              {status}
                            </span>
                          </div>
                          <div className="text-[9px] sm:text-[11px] leading-tight line-clamp-1 sm:line-clamp-2 break-words font-medium">
                            {t.title}
                          </div>
                        </div>
                      );
                    })}
                    {list.length > 4 && (
                      <div className="text-[8px] sm:text-[10px] text-slate-500 font-medium bg-slate-100 px-1 sm:px-2 py-0.5 sm:py-1 rounded-md text-center">
                        +{list.length - 4} more…
                      </div>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Task create modal */}
          <TaskModal
            isOpen={isTaskModalOpen}
            onClose={() => setIsTaskModalOpen(false)}
            users={users as any}
            teams={teams as any}
            currentUser={user as any}
            initialDueDate={undefined}
          />
        </div>
      </div>
    </div>
  );
}
